import { useEffect, useState } from 'react';
import type { CrosswordWord } from '../types';
import { wordLabel } from '../word-label';

interface WordToastProps {
  word: CrosswordWord | null;
  filledLetters: number;
  onDismiss: () => void;
}

// Clears the top bar's height so the toast never sits on top of the
// back/hamburger buttons before the visual-viewport effect below kicks in.
const BASE_TOP_OFFSET = 64;

// Mobile browsers pan the *visual* viewport independently of the layout
// viewport when the on-screen keyboard opens, so a plain `position: fixed`
// element (anchored to the layout viewport) can end up scrolled out of the
// visible area once a cell near the bottom of a tall board is focused.
// Tracking window.visualViewport keeps the toast pinned to what's actually
// on screen.
function useToastTop(): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const update = () => setOffset(viewport.offsetTop);
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return BASE_TOP_OFFSET + offset;
}

// Mobile-only: a compact clue readout that floats above the on-screen
// keyboard, since the full clue list is hidden on small screens in favor
// of screen space for the board itself.
export function WordToast({ word, filledLetters, onDismiss }: WordToastProps) {
  const top = useToastTop();
  if (!word) return null;
  return (
    <div className="word-toast" role="status" style={{ top }}>
      <div className="word-toast-body">
        <span className="word-toast-label">{wordLabel(word)}</span>
        <span className="word-toast-clue">{word.clue}</span>
      </div>
      <span className="word-toast-progress">
        {Math.min(filledLetters, word.length)}/{word.length}
      </span>
      <button aria-label="Close clue" className="word-toast-close" onClick={onDismiss} type="button">
        ×
      </button>
    </div>
  );
}
